import { memo, useEffect, useState } from 'react';
import { Alert, Dropdown, Space, Tooltip } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { dateToString } from '@/utils';
import {
  EditSvg,
  EllipsisVSvg,
  FileInvoiceSvg,
  InfoCircleSvg,
  LockSvg,
  MinusCircleSvg,
  PlusCircleSvg,
  PlusSvg,
  TrashSvg,
} from '@/assets/icons';
import Panel from '@/components/panel';
import Button from '@/components/button';
import {
  useGetParentAccountQuery,
  useGetParentFosterCareHasLicenseQuery,
  useGetParentRecommendationQuery,
  useGetParentRecommendationV2Query,
} from '@/services/parent-api';
import { ProfileCol, ProfileRow } from '@/components/profile-grid';
import LettersModal from './letters-modal';
import EditModal from './edit-modal';
import UpdateModal from './update-modal';
import SetPasswordModal from './set-password-modal';
import moment from 'moment';

const RecommendationInfo = ({ parentId, permissions, shouldRefetch, setShouldRefetch }) => {
  const { data: data1, refetch: refetch1, isLoading: loading1 } = useGetParentRecommendationV2Query({ parentId });
  const { data: data2, refetch: refetch2, isLoading: loading2 } = useGetParentRecommendationQuery({ parentId });
  const { data: data3, refetch: refetch3, isLoading: loading3 } = useGetParentAccountQuery({ parentId });
  const {
    data: hasLicense,
    refetch: refetch4,
    isLoading: loading4,
  } = useGetParentFosterCareHasLicenseQuery({ parentId });
  const loading = loading1 || loading2 || loading3 || loading4;

  const [editInfo, openEditModal] = useState();
  const [isOpenLettersModal, openLettersModal] = useState(false);
  const [isOpenPasswordModal, openPasswordModal] = useState(false);
  const [updateStatus, openUpdateModal] = useState();

  const refetch = () => {
    refetch1();
    refetch2();
    refetch3();
    refetch4();
  };

  useEffect(() => {
    if (shouldRefetch) {
      refetch4();
      setShouldRefetch(false);
    }
  }, [shouldRefetch, setShouldRefetch, refetch4]);

  return (
    <Panel
      icon={<InfoCircleSvg />}
      title="Recommendation Information"
      extra={
        !loading &&
        (data1.recommended || data1?.onHold ? (
          permissions.writeParent ? (
            <Dropdown
              menu={{
                items: [
                  data1?.onHold && permissions.adminStaffAndSuper && {
                    key: 'add',
                    icon: <PlusSvg />,
                    label: 'Recommend Parent',
                    onClick: () => openEditModal({}),
                  },
                  data2 && permissions.adminStaffAndSuper && {
                    key: 'edit',
                    icon: <EditSvg />,
                    label: 'Edit Recommendation',
                    onClick: () => openEditModal(data2),
                  },
                  data2 && {
                    key: 'view',
                    icon: <FileInvoiceSvg />,
                    label: 'View Letters',
                    onClick: () => openLettersModal(data2.parentRecommendationHistoryId),
                  },
                  permissions.adminStaffAndSuper && (data1?.onHold 
                    ? {
                        key: 'hold',
                        icon: <PlusCircleSvg />,
                        label: 'Remove Hold',
                        onClick: () => openUpdateModal('removeHold'),
                      }
                    : {
                        key: 'hold',
                        icon: <MinusCircleSvg />,
                        label: 'Put On Hold',
                        onClick: () => openUpdateModal('putHold'),
                      }),
                  data3 && permissions.adminStaffAndSuper && {
                    key: 'password',
                    icon: <LockSvg />,
                    label: 'Set Password',
                    onClick: () => openPasswordModal(true),
                  },
                  permissions.adminStaffAndSuper && {
                    key: 'remove',
                    danger: true,
                    icon: <TrashSvg />,
                    label: 'Remove Recommendation Information',
                    onClick: () => openUpdateModal('delete'),
                  },
                ].filter((item) => item),
              }}
            >
              <Button type="link" icon={<EllipsisVSvg fontSize="16" />} />
            </Dropdown>
          ) : (
            data2 && (
              <Tooltip title="View Letters">
                <Button
                  type="link"
                  icon={<FileInvoiceSvg fontSize="16" />}
                  onClick={() => openLettersModal(data2.parentRecommendationHistoryId)}
                />
              </Tooltip>
            )
          )
        ) : (
          permissions.writeParent && permissions.adminStaffAndSuper && (
            <Tooltip title="Recommend Parent">
              <Button type="link" icon={<PlusSvg fontSize="16" />} onClick={() => openEditModal({})} />
            </Tooltip>
          )
        ))
      }
      loading={loading}
    >
      {data1?.onHold && (
        <Alert
          message="On Hold"
          type="error"
          showIcon
          icon={<ExclamationCircleFilled />}
          style={{ marginBottom: 16 }}
        />
      )}

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <ProfileRow>
          {data1 && <ProfileCol md={12} lg={8} label="Recommended" value={data1.recommended ? 'Yes' : 'No'} />}
          {data2 && <ProfileCol md={12} lg={8} label="Type" value={data2.recommendationType.name} />}
          {data2 && (
            <ProfileCol md={12} lg={8} label="Expires" value={moment(data2.expires, 'YYYY-MM-DD').format('M/D/YYYY')} />
          )}
        </ProfileRow>
        <ProfileRow>
          {data3 && data1?.recommended && (
            <>
              <ProfileCol md={12} lg={8} label="Username" value={data3.userName} />
              <ProfileCol md={12} lg={8} label="Last Login" value={dateToString(data3.lastLogin, 'M/DD/YYYY h:mm A')} />
              <ProfileCol md={12} lg={8} label="Portal Inquiries" value={data3.numberOfInquiries} />
            </>
          )}
        </ProfileRow>
      </Space>

      <EditModal
        parentId={parentId}
        editInfo={editInfo}
        hasLicense={hasLicense}
        refetch={refetch}
        onClose={() => openEditModal()}
      />

      <LettersModal
        parentId={parentId}
        recommendationHistoryId={data2?.parentRecommendationHistoryId}
        open={isOpenLettersModal}
        onClose={() => openLettersModal()}
      />

      <SetPasswordModal userId={data3?.userId} open={isOpenPasswordModal} onClose={() => openPasswordModal(false)} />

      <UpdateModal parentId={parentId} status={updateStatus} refetch={refetch} onClose={() => openUpdateModal()} />
    </Panel>
  );
};

export default memo(RecommendationInfo);
